import {
  FillBlankComponent,
  LessonComponentResponseType
} from '../../../models/LessonComponent';

import { BlankItem } from '../../../models/LessonComponentContent';
import Checkbox from '@mui/material/Checkbox';
import DeleteIcon from '@mui/icons-material/Delete';
import FillBlankView from './FillBlankView';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Unstable_Grid2';
import { IconButtonWithConfirmation } from '../../../../shared/components/IconButtonWithConfirmation';
import { InlineTextEdit } from '../../InlineTextEdit';
import ItemEditor from '../ItemEditor';
import React from 'react';
import Typography from '@mui/material/Typography';
import { cloneDeep } from 'lodash';
import styles from '../../../styles/FillBlank.module.css';
import { useLessonContext } from '../../../models/LessonContext';
import { useLessonEditContext } from '../../../models/LessonEditContext';
import useThemes from '../../../../shared/hooks/useThemes';

interface FillBlankProps {
  component: FillBlankComponent;
  onChange: (response: LessonComponentResponseType) => void;
}

export default function FillBlank(props: FillBlankProps) {
  const { muiTheme } = useThemes();
  const fontFamily = muiTheme.typography.fontFamily;
  const { handleSettingsChange } = useLessonEditContext();

  const { mode } = useLessonContext();

  const blanks = props.component?.content.blank;
  const withMenus = props.component.type === 'fill_menu';

  const handleTextChange = (value: string) => {
    const copy = cloneDeep(props.component);
    copy.content.text = value;

    const dom = new DOMParser().parseFromString(value, 'text/html');
    const blankNodes = dom.querySelectorAll('.fill-the-blank');

    let blankIds: string[] = [];
    blankNodes.forEach((b) => {
      blankIds.push(b.id);
    });
    //Filter blanks and update component state when any blank is removed
    const updatedBlanks = copy.content.blank.filter((b) =>
      blankIds.includes(b.id)
    );
    copy.content.blank = updatedBlanks;
    handleSettingsChange(copy);
  };

  const handleItemChange = (options: BlankItem[]) => {
    const copy = cloneDeep(props.component);
    copy.content.blank = options;
    handleSettingsChange(copy);
  };

  const handleClick = (value: string, id: string) => {
    //update content value in the editComponent state
    const copy = cloneDeep(props.component);
    copy.content.text = value;

    //create new blank item/option
    const blankItem: BlankItem = {
      id: id,
      options: [],
      matchCase: false
    };
    copy.content.blank.push(blankItem);

    handleSettingsChange(copy);
  };

  const handleAddOption = (option: string, id: string) => {
    const res = props.component.content.blank.find((c) => c.id === id);
    handleOptionChange(option, res.options.length, id);
  };

  const handleOptionChange = (option: string, idx: number, id: string) => {
    const copy = cloneDeep(props.component);
    const res = copy.content.blank.find((c) => c.id === id);
    res.options[idx] = option;
    if (withMenus && idx === 0) {
      const corrects = JSON.parse(copy.content.correctResponsePattern);
      corrects[id] = option;
      copy.content.correctResponsePattern = JSON.stringify(corrects);
    }
    handleSettingsChange(copy);
  };

  const handleMatchcaseChange = (checked: boolean, id: string) => {
    const copy = cloneDeep(blanks);
    const index = copy.findIndex((c) => c.id === id);
    copy[index].matchCase = checked;
    handleItemChange(copy);
  };

  const handleDeleteBlank = (id: string) => {
    const copy = cloneDeep(props.component);
    const blankIndex = copy.content.blank.findIndex((b) => b.id === id);
    copy.content.blank.splice(blankIndex, 1);

    //Remove the deleted blank item from content text
    const dom = new DOMParser().parseFromString(copy.content.text, 'text/html');
    const node = dom.querySelector(`.fill-the-blank[id="${id}"]`);
    if (node) {
      node.parentNode.removeChild(node);
      copy.content.text = dom.body.outerHTML;
    }

    if (withMenus) {
      const corrects = JSON.parse(copy.content.correctResponsePattern);
      delete corrects[id];
      copy.content.correctResponsePattern = JSON.stringify(corrects);
    }
    handleSettingsChange(copy);
  };

  const handleDeleteOption = (id: string, index: number) => {
    const copy = cloneDeep(props.component);
    const blankItem = copy.content.blank?.find((b) => b.id === id);
    blankItem.options.splice(index, 1);
    handleSettingsChange(copy);
  };

  return (
    <Grid container direction="column" style={{ fontFamily }}>
      <Grid className={`${styles.blankContent} ${styles.componentSection}`}>
        {mode === 'edit' ? (
          <ItemEditor
            componentkey={props.component.componentkey}
            value={props.component.content.text}
            styleType="noLink"
            placeholder="Click to add text"
            onChange={handleTextChange}
            onClick={handleClick}
          />
        ) : (
          <div className="style-noLink">
            <FillBlankView
              component={props.component}
              onChange={props.onChange}
            />
          </div>
        )}
      </Grid>

      {mode === 'edit' &&
        !props.component.settings.survey &&
        blanks.map((c, index) => (
          <Grid
            container
            key={index}
            direction="column"
            className={styles.componentSection}
          >
            <Grid container alignItems="center">
              <Grid>
                <Typography component="span" className={styles.blank}>{`${
                  withMenus ? 'Selector' : 'Blank'
                } ${index + 1}`}</Typography>
              </Grid>
              <Grid>
                <IconButtonWithConfirmation
                  size="small"
                  onConfirm={() => handleDeleteBlank(c.id)}
                >
                  <DeleteIcon />
                </IconButtonWithConfirmation>
              </Grid>
            </Grid>
            {withMenus && (
              <Grid container direction="column">
                <Grid className={styles.optionsHeaderGrid}>
                  <Typography
                    component="span"
                    className={styles.optionsHeaderLabel}
                  >
                    Correct answer
                  </Typography>
                </Grid>
                <Grid className={styles.blankOptionsContainer} md={6}>
                  <InlineTextEdit
                    value={c.options[0]}
                    className={styles.blankOptionTextStyle}
                    placeholder="click to add correct answer"
                    onChange={(value) => handleOptionChange(value, 0, c.id)}
                  />
                </Grid>
                <Grid className={styles.optionsHeaderGrid}>
                  <Typography
                    component="span"
                    className={styles.optionsHeaderLabel}
                  >
                    Incorrect answers
                  </Typography>
                </Grid>
              </Grid>
            )}
            <Grid
              container
              direction="column"
              className={styles.blankOptionsContainer}
            >
              {c.options.map((o, idx) => {
                return (
                  (!withMenus || (withMenus && idx > 0)) && (
                    <Grid
                      container
                      alignItems="center"
                      key={idx}
                      className={styles.blankOptionItemContainer}
                    >
                      <Grid xs={10} md={6} style={{ paddingRight: 5 }}>
                        <InlineTextEdit
                          value={o}
                          className={styles.blankOptionTextStyle}
                          placeholder="click to edit an option"
                          onChange={(value) =>
                            handleOptionChange(value, idx, c.id)
                          }
                        />
                      </Grid>
                      <Grid xs={2} md={6}>
                        <IconButtonWithConfirmation
                          size="small"
                          onConfirm={() => handleDeleteOption(c.id, idx)}
                        >
                          <DeleteIcon />
                        </IconButtonWithConfirmation>
                      </Grid>
                    </Grid>
                  )
                );
              })}

              <Grid xs={12} md={6} className={styles.blankOptionItemContainer}>
                {mode === 'edit' &&
                  (!withMenus || (withMenus && c.options.length > 0)) && (
                    <InlineTextEdit
                      value={''}
                      className={styles.blankOptionTextStyle}
                      placeholder={
                        withMenus
                          ? 'Click to add incorrect answer'
                          : 'Click to add an option'
                      }
                      onChange={(value) => handleAddOption(value, c.id)}
                    />
                  )}
              </Grid>
              {!withMenus && (
                <Grid>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={c.matchCase}
                        onChange={(e, checked) =>
                          handleMatchcaseChange(checked, c.id)
                        }
                        color="primary"
                      />
                    }
                    label="Case-sensitive"
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        ))}
    </Grid>
  );
}
